import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Target,
  ArrowUpDown,
  Check,
  AlertTriangle,
  Cpu,
  ExternalLink,
} from "lucide-react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { getWearableByMac } from "constants/wearables";

// Unit identity line under the athlete's name (superadmins only — `unit` is
// only populated from the admin roster endpoint). Links to the manufacturing
// record when the MAC matches a known unit.
const UnitLine = ({ unit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const label =
    unit.fieldLabel != null
      ? String(unit.fieldLabel).padStart(3, "0")
      : unit.serial;
  const content = (
    <>
      <Cpu size={10} className="flex-shrink-0" />
      <span className="truncate">
        {label ? `${label} · ` : ""}
        {unit.mac}
      </span>
    </>
  );

  if (!unit.bleMac) {
    return (
      <p className="mt-0.5 flex items-center gap-1 font-mono text-[10px] text-[#9CA3AF]">
        {content}
      </p>
    );
  }
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/v2/admin/manufacturing/wearables/${unit.bleMac}`);
      }}
      title={t("session.playerList.openUnit")}
      className="mt-0.5 flex items-center gap-1 font-mono text-[10px] text-[#9CA3AF] transition-colors duration-150 hover:text-[#E07A3A]"
    >
      {content}
      <ExternalLink size={9} className="flex-shrink-0" />
    </button>
  );
};

const getInitials = (name) => {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const SORT_OPTIONS = [
  { id: "recent", labelKey: "session.playerList.sortOptions.recent" },
  { id: "impacts", labelKey: "session.playerList.sortOptions.impacts" },
  { id: "name", labelKey: "session.playerList.sortOptions.name" },
];

const sortPlayers = (players, mode) => {
  const copy = [...players];
  switch (mode) {
    case "name":
      return copy.sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
    case "impacts":
      return copy.sort(
        (a, b) =>
          (b.impacts || 0) - (a.impacts || 0) || (b.peakG || 0) - (a.peakG || 0)
      );
    case "recent":
    default:
      return copy.sort(
        (a, b) =>
          (b.lastImpactTs || 0) - (a.lastImpactTs || 0) ||
          (b.impacts || 0) - (a.impacts || 0)
      );
  }
};

const SessionPlayerList = ({
  players,
  selectedPlayerId,
  onPlayerSelect,
  maxPeakG,
  tracksHeaders = false,
  playerHeaderCounts = {},
  playerHeaderStats = {},
  showHeaderDetails = false,
  flashingPlayerId = null,
}) => {
  const { t } = useTranslation();
  const [sortMode, setSortMode] = useState("recent");
  const [showSortMenu, setShowSortMenu] = useState(false);

  const sortedPlayers = useMemo(
    () => sortPlayers(players, sortMode),
    [players, sortMode]
  );

  const sortOption = SORT_OPTIONS.find((o) => o.id === sortMode);
  const sortLabel = sortOption
    ? t(sortOption.labelKey)
    : t("session.playerList.sort");

  return (
    <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("session.playerList.title")}
          </h3>
          <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
            {players.length}
          </span>
        </div>

        {/* Sort selector */}
        <div className="relative">
          <button
            onClick={() => setShowSortMenu((v) => !v)}
            onBlur={() => setTimeout(() => setShowSortMenu(false), 120)}
            className="flex items-center gap-1.5 rounded-md border border-[#E5E7EB] bg-white px-2 py-1 text-[11px] font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
          >
            <ArrowUpDown size={11} />
            {sortLabel}
          </button>
          {showSortMenu && (
            <div className="absolute right-0 top-full z-10 mt-1 min-w-[140px] overflow-hidden rounded-lg border border-[#F3F4F6] bg-white shadow-lg">
              {SORT_OPTIONS.map((opt) => {
                const active = opt.id === sortMode;
                return (
                  <button
                    key={opt.id}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      setSortMode(opt.id);
                      setShowSortMenu(false);
                    }}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-[12px] transition duration-150 hover:bg-[#F9FAFB] ${
                      active ? "font-semibold text-[#E07A3A]" : "text-[#374151]"
                    }`}
                  >
                    {t(opt.labelKey)}
                    {active && <Check size={12} />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable Player List — fills whatever height the card is given */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {sortedPlayers.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB]">
              <Users size={22} className="text-[#6B7280]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("session.playerList.emptyTitle")}
            </h3>
            <p className="max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("session.playerList.emptyBody")}
            </p>
          </div>
        ) : (
          sortedPlayers.map((player) => {
            const isSelected = player.id === selectedPlayerId;
            const isFlashing = player.id === flashingPlayerId;
            const barWidth =
              maxPeakG > 0 ? Math.max((player.peakG / maxPeakG) * 100, 4) : 0;
            const wearable = player.wearableMac
              ? getWearableByMac(player.wearableMac)
              : null;
            const hs = playerHeaderStats[player.id] || {};
            const headerCount =
              hs.headerCount ?? playerHeaderCounts[player.id] ?? 0;

            return (
              <motion.div
                key={player.id}
                layout
                transition={{ type: "spring", stiffness: 400, damping: 32 }}
                onClick={() => onPlayerSelect?.(player)}
                style={
                  isFlashing
                    ? { animation: "rowFlash 800ms ease-out" }
                    : undefined
                }
                className={`flex cursor-pointer items-center gap-3 border-b border-[#F3F4F6] px-4 py-3 transition-colors duration-150 last:border-b-0 ${
                  isSelected
                    ? "border-l-2 border-l-[#E07A3A] bg-[#FEF7F3]"
                    : "border-l-transparent border-l-2 hover:bg-[#FAFAFA]"
                }`}
              >
                {/* Avatar */}
                <div
                  className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full ${
                    isSelected ? "bg-[#E07A3A]" : "bg-[#F3F4F6]"
                  }`}
                >
                  <span
                    className={`text-xs font-semibold ${
                      isSelected ? "text-white" : "text-[#6B7280]"
                    }`}
                  >
                    {getInitials(player.name)}
                  </span>
                </div>

                {/* Player Info */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-[13px] font-semibold text-[#111827]">
                      {player.name}
                    </p>
                    {showHeaderDetails && hs.hasCluster && (
                      <span title={t("session.playerList.clusterHint")}>
                        <AlertTriangle size={12} className="text-[#E07A3A]" />
                      </span>
                    )}
                    {!player.unit && (wearable || player.wearableMac) && (
                      <span
                        className="flex-shrink-0 rounded bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-medium text-[#6B7280]"
                        title={player.wearableMac}
                      >
                        {wearable
                          ? `#${wearable.id}`
                          : t("session.playerList.wearableBadge")}
                      </span>
                    )}
                  </div>
                  {player.unit && <UnitLine unit={player.unit} />}
                  {showHeaderDetails && tracksHeaders ? (
                    <div className="mt-1 flex items-center gap-3">
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-[#E07A3A]">
                        <Target size={10} />
                        {t("session.playerList.headerCount", {
                          count: headerCount,
                        })}
                      </span>
                      <span className="text-[11px] text-[#9CA3AF]">
                        {t("session.playerList.avgG", {
                          value: (hs.avgG ?? 0).toFixed(1),
                        })}
                      </span>
                      <span className="text-[11px] text-[#9CA3AF]">
                        {t("session.playerList.peakG", {
                          value: (hs.peakG ?? 0).toFixed(1),
                        })}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-1 flex items-center gap-3">
                      <span className="text-[11px] text-[#9CA3AF]">
                        {t("session.playerList.impacts", {
                          value: player.impacts,
                        })}
                      </span>
                      <span className="text-[11px] text-[#9CA3AF]">
                        {t("session.playerList.peakLabel", {
                          value: player.peakG.toFixed(1),
                        })}
                      </span>
                      {tracksHeaders && (
                        <span className="flex items-center gap-0.5 text-[11px] text-[#9CA3AF]">
                          <Target size={10} />
                          {headerCount}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Peak G bar */}
                  {maxPeakG > 0 && (
                    <div className="mt-1.5 h-1 w-full rounded-full bg-[#F3F4F6]">
                      <div
                        className="h-1 rounded-full transition-all duration-300"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: isSelected ? "#E07A3A" : "#D1D5DB",
                        }}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      <style>{`
        @keyframes rowFlash {
          0% { background-color: rgba(224, 122, 58, 0.45); }
          100% { background-color: transparent; }
        }
      `}</style>
    </div>
  );
};

export default SessionPlayerList;
